import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import { firmaApi } from '../services/api.ts';

const steps = ['Verificar Link', 'Subir Documento', 'Firmar Documento'];

const FirmaPorLink: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [linkData, setLinkData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [firmaResultado, setFirmaResultado] = useState<any>(null);

  useEffect(() => {
    if (token) {
      cargarLinkData();
    }
  }, [token]);

  const cargarLinkData = async () => {
    try {
      setLoading(true);
      const data = await firmaApi.obtenerLinkPorToken(token!);
      setLinkData(data);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar el link');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Por favor, selecciona un archivo PDF');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleFirmarDocumento = async () => {
    if (!selectedFile || !linkData) return;

    try {
      setLoading(true);
      const base64Document = await convertFileToBase64(selectedFile);
      
      const firmaData = {
        firmante: linkData.nombre,
        email: linkData.email,
        documento: base64Document,
        titulo: linkData.titulo,
        descripcion: linkData.descripcion,
        verificacionId: linkData.verificacionId,
      };

      const resultado = await firmaApi.firmarDocumento(firmaData);
      setFirmaResultado(resultado);
      setActiveStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al firmar documento');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !linkData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !linkData) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Error</Typography>
          <Typography>{error}</Typography>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => navigate('/')}
          >
            Volver al Inicio
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Firma Electrónica
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {linkData && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Documento
              </Typography>
              <Typography><strong>Firmante:</strong> {linkData.nombre}</Typography>
              <Typography><strong>Email:</strong> {linkData.email}</Typography>
              <Typography><strong>Documento:</strong> {linkData.titulo}</Typography>
              {linkData.descripcion && (
                <Typography><strong>Descripción:</strong> {linkData.descripcion}</Typography>
              )}
            </CardContent>
          </Card>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Subir Documento PDF
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Documento PDF *
                </Typography>
                <input
                  accept=".pdf"
                  style={{ display: 'none' }}
                  id="pdf-file-input"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="pdf-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Seleccionar archivo PDF
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" color="text.secondary">
                    Archivo seleccionado: {selectedFile.name}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || !selectedFile}
                  onClick={handleFirmarDocumento}
                >
                  {loading ? <CircularProgress size={24} /> : 'Proceder con la Firma'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeStep === 2 && firmaResultado && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Documento Enviado para Firma
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="h6">¡Éxito!</Typography>
              <Typography>Tu documento ha sido enviado a Adobe Sign para la firma electrónica.</Typography>
            </Alert>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalles de la Firma
                </Typography>
                <Typography><strong>ID:</strong> {firmaResultado.id}</Typography>
                <Typography><strong>Estado:</strong> {firmaResultado.estado}</Typography>
                <Typography><strong>Firmante:</strong> {firmaResultado.firmante}</Typography>
                <Typography><strong>Timestamp:</strong> {firmaResultado.timestamp}</Typography>
                
                {firmaResultado.url && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => window.open(firmaResultado.url, '_blank')}
                    >
                      Ir a Adobe Sign
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default FirmaPorLink; 